// Reads just enough of an image file to know what it is, and removes what it says about the
// person: the place it was taken, the camera, the time and any comments. Only headers are read,
// and the rest of the file is passed through as a slice of the original Blob, so a large photo is
// never held in memory here.

export type ImageFormat = "jpeg" | "png" | "webp" | "gif" | "heic" | "avif" | "other";

async function bytesOf(blob: Blob, start: number, end: number): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await blob.slice(start, end).arrayBuffer());
}

const ascii = (b: Uint8Array, start: number, len: number) =>
  String.fromCharCode(...b.subarray(start, start + len));

const HEIF_BRANDS = new Set(["heic", "heix", "hevc", "hevx", "heim", "heis", "hevm", "hevs", "mif1", "msf1"]);

/** What an image file is, from its first bytes. The file name and type are not trusted. */
export async function sniff(blob: Blob): Promise<ImageFormat> {
  const b = await bytesOf(blob, 0, 64);
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "jpeg";
  if (b[0] === 0x89 && ascii(b, 1, 3) === "PNG") return "png";
  if (ascii(b, 0, 4) === "RIFF" && ascii(b, 8, 4) === "WEBP") return "webp";
  if (ascii(b, 0, 4) === "GIF8") return "gif";
  if (ascii(b, 4, 4) === "ftyp") {
    const size = new DataView(b.buffer, b.byteOffset).getUint32(0);
    const brands = [ascii(b, 8, 4)];
    for (let o = 16; o + 4 <= Math.min(size, b.length); o += 4) brands.push(ascii(b, o, 4));
    if (brands.includes("avif") || brands.includes("avis")) return "avif";
    if (brands.some((x) => HEIF_BRANDS.has(x))) return "heic";
  }
  return "other";
}

/** Whether a file is HEIC or HEIF, by its bytes, its type or its name. */
export async function isHeic(file: Blob): Promise<boolean> {
  if ((await sniff(file)) === "heic") return true;
  const name = (file as File).name?.toLowerCase() ?? "";
  return /image\/hei[cf]/.test(file.type) || name.endsWith(".heic") || name.endsWith(".heif");
}

// JPEG

export type JpegInfo = {
  width: number;
  height: number;
  /** EXIF orientation, 1 to 8. 1 when there is none. */
  orientation: number;
  /** Where the first scan starts. Everything before it is headers. */
  scanStart: number;
};

type Segment = { marker: number; start: number; end: number; data: Uint8Array | null };

/** Reads the header segments of a JPEG, up to the first scan. Keeps data only for the small ones it needs. */
async function jpegSegments(blob: Blob): Promise<{ segments: Segment[]; scanStart: number }> {
  const soi = await bytesOf(blob, 0, 2);
  if (soi[0] !== 0xff || soi[1] !== 0xd8) throw new Error("Not a JPEG");
  const segments: Segment[] = [];
  let offset = 2;
  for (;;) {
    let head = await bytesOf(blob, offset, offset + 4);
    if (head.length < 2) throw new Error("This JPEG ends early.");
    // Fill bytes before a marker are allowed.
    while (head[0] === 0xff && head[1] === 0xff) {
      offset += 1;
      head = await bytesOf(blob, offset, offset + 4);
    }
    if (head[0] !== 0xff) throw new Error("This JPEG is damaged.");
    const marker = head[1] as number;
    if (marker === 0xda) return { segments, scanStart: offset };
    if (marker === 0xd9) throw new Error("This JPEG has no image.");
    // Markers with no length.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      offset += 2;
      continue;
    }
    if (head.length < 4) throw new Error("This JPEG ends early.");
    const len = ((head[2] as number) << 8) | (head[3] as number);
    const end = offset + 2 + len;
    const wanted =
      marker === 0xe1 ||
      (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc);
    const data = wanted ? await bytesOf(blob, offset + 4, end) : null;
    segments.push({ marker, start: offset, end, data });
    offset = end;
  }
}

/** Orientation from an EXIF block ("Exif\0\0" and a TIFF header), or 1. */
export function exifOrientation(app1: Uint8Array): number {
  if (ascii(app1, 0, 6) !== "Exif\0\0") return 1;
  const tiff = app1.subarray(6);
  if (tiff.length < 8) return 1;
  const little = tiff[0] === 0x49;
  const view = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength);
  const u16 = (o: number) => view.getUint16(o, little);
  const u32 = (o: number) => view.getUint32(o, little);
  try {
    const ifd = u32(4);
    const count = u16(ifd);
    for (let i = 0; i < count; i++) {
      const entry = ifd + 2 + i * 12;
      if (u16(entry) === 0x0112) {
        const value = u16(entry + 8);
        return value >= 1 && value <= 8 ? value : 1;
      }
    }
  } catch {
    // A damaged block says nothing.
  }
  return 1;
}

export async function jpegInfo(blob: Blob): Promise<JpegInfo> {
  const { segments, scanStart } = await jpegSegments(blob);
  let orientation = 1;
  let width = 0;
  let height = 0;
  for (const s of segments) {
    if (s.marker === 0xe1 && s.data && orientation === 1) orientation = exifOrientation(s.data);
    if (s.marker >= 0xc0 && s.marker <= 0xcf && s.data && s.data.length >= 5) {
      height = ((s.data[1] as number) << 8) | (s.data[2] as number);
      width = ((s.data[3] as number) << 8) | (s.data[4] as number);
    }
  }
  return { width, height, orientation, scanStart };
}

/** A minimal EXIF block that only says how to turn the picture. */
export function orientationExif(orientation: number): Uint8Array<ArrayBuffer> {
  const body = new Uint8Array([
    // "Exif\0\0", then a big-endian TIFF header with IFD0 at offset 8.
    0x45,
    0x78,
    0x69,
    0x66,
    0,
    0,
    0x4d,
    0x4d,
    0,
    0x2a,
    0,
    0,
    0,
    8,
    // One entry: Orientation, SHORT, count 1, the value, padding. Then no next IFD.
    0,
    1,
    0x01,
    0x12,
    0,
    3,
    0,
    0,
    0,
    1,
    0,
    orientation,
    0,
    0,
    0,
    0,
    0,
    0,
  ]);
  const seg = new Uint8Array(body.length + 4);
  seg.set([0xff, 0xe1, ((body.length + 2) >> 8) & 0xff, (body.length + 2) & 0xff]);
  seg.set(body, 4);
  return seg;
}

/** Where the image data ends: just after the first end-of-image marker after `from`. */
async function jpegEnd(blob: Blob, from: number): Promise<number> {
  const step = 1024 * 1024;
  let prevFF = false;
  for (let start = from; start < blob.size; start += step) {
    const b = await bytesOf(blob, start, start + step);
    for (let i = 0; i < b.length; i++) {
      if (prevFF && b[i] === 0xd9) return start + i + 1;
      prevFF = b[i] === 0xff;
    }
  }
  return blob.size;
}

/**
 * The JPEG without EXIF, XMP, IPTC, comments or the extra pictures some phones add after it. The
 * colour profile and the Adobe marker are kept, so colours do not change, and the pixels are not
 * touched. With `keepOrientation`, a minimal EXIF block keeps only the rotation.
 */
export async function stripJpeg(blob: Blob, keepOrientation = true): Promise<Blob> {
  const { segments, scanStart } = await jpegSegments(blob);
  let orientation = 1;
  const kept: (Uint8Array | Blob)[] = [new Uint8Array([0xff, 0xd8])];
  let insertAt = 1;
  for (const s of segments) {
    if (s.marker === 0xe1 && s.data && orientation === 1) orientation = exifOrientation(s.data);
    const keep =
      s.marker === 0xe0 || // JFIF
      (s.marker === 0xe2 &&
        (await bytesOf(blob, s.start + 4, s.start + 16)).every(
          (c, i) => c === "ICC_PROFILE\0".charCodeAt(i),
        )) ||
      s.marker === 0xee || // Adobe colour transform
      s.marker < 0xe0 || // tables and frame headers
      (s.marker > 0xef && s.marker !== 0xfe);
    if (!keep) continue;
    kept.push(blob.slice(s.start, s.end));
    if (s.marker === 0xe0) insertAt = kept.length;
  }
  if (keepOrientation && orientation !== 1) kept.splice(insertAt, 0, orientationExif(orientation));
  const end = await jpegEnd(blob, scanStart);
  kept.push(blob.slice(scanStart, end));
  return new Blob(kept as BlobPart[], { type: "image/jpeg" });
}

// PNG

export type PngInfo = { width: number; height: number; alpha: boolean; animated: boolean };

type Chunk = { type: string; start: number; end: number };

async function pngChunks(blob: Blob): Promise<{ chunks: Chunk[]; header: Uint8Array }> {
  const sig = await bytesOf(blob, 0, 8);
  if (sig[0] !== 0x89 || ascii(sig, 1, 3) !== "PNG") throw new Error("Not a PNG");
  const chunks: Chunk[] = [];
  let header = new Uint8Array();
  let offset = 8;
  while (offset + 8 <= blob.size) {
    const head = await bytesOf(blob, offset, offset + 8);
    const len = new DataView(head.buffer).getUint32(0);
    const type = ascii(head, 4, 4);
    const end = offset + 12 + len;
    if (end > blob.size) throw new Error("This PNG ends early.");
    if (type === "IHDR") header = await bytesOf(blob, offset + 8, offset + 8 + len);
    chunks.push({ type, start: offset, end });
    offset = end;
    if (type === "IEND") break;
  }
  return { chunks, header };
}

export async function pngInfo(blob: Blob): Promise<PngInfo> {
  const { chunks, header } = await pngChunks(blob);
  const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
  const colorType = header[9];
  return {
    width: header.length >= 8 ? view.getUint32(0) : 0,
    height: header.length >= 8 ? view.getUint32(4) : 0,
    alpha: colorType === 4 || colorType === 6 || chunks.some((c) => c.type === "tRNS"),
    animated: chunks.some((c) => c.type === "acTL"),
  };
}

const PNG_PRIVATE = new Set(["tEXt", "zTXt", "iTXt", "eXIf", "tIME"]);

/** The PNG without text, EXIF or time chunks. Colour chunks such as iCCP and sRGB stay. */
export async function stripPng(blob: Blob): Promise<Blob> {
  const { chunks } = await pngChunks(blob);
  const parts: BlobPart[] = [blob.slice(0, 8)];
  for (const c of chunks) if (!PNG_PRIVATE.has(c.type)) parts.push(blob.slice(c.start, c.end));
  return new Blob(parts, { type: "image/png" });
}

// WebP

export type WebpInfo = { alpha: boolean; animated: boolean };

async function webpChunks(blob: Blob): Promise<Chunk[]> {
  const head = await bytesOf(blob, 0, 12);
  if (ascii(head, 0, 4) !== "RIFF" || ascii(head, 8, 4) !== "WEBP") throw new Error("Not a WebP");
  const chunks: Chunk[] = [];
  let offset = 12;
  while (offset + 8 <= blob.size) {
    const h = await bytesOf(blob, offset, offset + 8);
    const len = new DataView(h.buffer).getUint32(4, true);
    const end = offset + 8 + len + (len % 2);
    chunks.push({ type: ascii(h, 0, 4), start: offset, end: Math.min(end, blob.size) });
    offset = end;
  }
  return chunks;
}

export async function webpInfo(blob: Blob): Promise<WebpInfo> {
  const chunks = await webpChunks(blob);
  const vp8x = chunks.find((c) => c.type === "VP8X");
  if (vp8x) {
    const flags = (await bytesOf(blob, vp8x.start + 8, vp8x.start + 9))[0] ?? 0;
    return { alpha: Boolean(flags & 0x10), animated: Boolean(flags & 0x02) };
  }
  const lossless = chunks.find((c) => c.type === "VP8L");
  if (lossless) {
    // The alpha hint is bit 28 of the 32 bits after the signature byte.
    const b = await bytesOf(blob, lossless.start + 9, lossless.start + 13);
    return { alpha: Boolean(((b[3] ?? 0) >> 4) & 1), animated: false };
  }
  return { alpha: false, animated: false };
}

/** The WebP without EXIF or XMP chunks. */
export async function stripWebp(blob: Blob): Promise<Blob> {
  const chunks = await webpChunks(blob);
  if (!chunks.some((c) => c.type === "EXIF" || c.type === "XMP ")) return blob;
  const parts: BlobPart[] = [];
  let size = 4;
  for (const c of chunks) {
    if (c.type === "EXIF" || c.type === "XMP ") continue;
    if (c.type === "VP8X") {
      const vp8x = await bytesOf(blob, c.start, c.end);
      vp8x[8] = (vp8x[8] ?? 0) & ~0x0c;
      parts.push(vp8x);
    } else parts.push(blob.slice(c.start, c.end));
    size += c.end - c.start;
  }
  const riff = new Uint8Array(12);
  riff.set([0x52, 0x49, 0x46, 0x46]);
  new DataView(riff.buffer).setUint32(4, size, true);
  riff.set([0x57, 0x45, 0x42, 0x50], 8);
  return new Blob([riff, ...parts], { type: "image/webp" });
}
